import { api, cacheMinTimer, thunkLoader, thunks } from "@app/api";
import { call, createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployPersistentDisk,
  DeployPersistentDiskResponse,
  HalEmbedded,
} from "@app/types";
import type { DeployOperationResponse } from "../operation";

export const defaultPersistentDiskResponse = (
  d: Partial<DeployPersistentDiskResponse> = {},
): DeployPersistentDiskResponse => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    size: 10,
    disk_type: "gp3",
    status: "pending",
    created_at: now,
    updated_at: now,
    _type: "persistent_disk",
    ...d,
    _links: {
      account: defaultHalHref(),
      ...d._links,
    },
  };
};

export const deserializePersistentDisk = (
  payload: DeployPersistentDiskResponse,
): DeployPersistentDisk => {
  return {
    id: `${payload.id}`,
    handle: payload.handle,
    size: payload.size,
    diskType: payload.disk_type,
    status: payload.status,
    environmentId: extractIdFromLink(payload._links.account),
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

export const selectPersistentDiskById = schema.persistentDisks.selectById;
export const selectPersistentDisks = schema.persistentDisks.selectTable;
export const selectPersistentDisksAsList =
  schema.persistentDisks.selectTableAsList;
export const findPersistentDiskById = schema.persistentDisks.findById;

export const findPersistentDisksByEnvId = (
  disks: DeployPersistentDisk[],
  envId: string,
) =>
  disks
    .filter((d) => d.environmentId === envId)
    .sort((a, b) => a.handle.localeCompare(b.handle));

export const selectPersistentDisksByEnvId = createSelector(
  selectPersistentDisksAsList,
  (_: WebState, p: { envId: string }) => p.envId,
  findPersistentDisksByEnvId,
);

// Org-wide list (already exists on deploy-api).
export const fetchPersistentDisks = api.get(
  "/persistent_disks?per_page=5000",
  {
    supervisor: cacheMinTimer(),
  },
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    yield* schema.update(schema.persistentDisks.reset());
  },
);

export const fetchPersistentDisk = api.get<{ id: string }>(
  "/persistent_disks/:id",
);

export const fetchPersistentDisksByEnvId = api.get<
  { envId: string },
  HalEmbedded<{ persistent_disks: DeployPersistentDiskResponse[] }>
>("/accounts/:envId/persistent_disks");

export interface CreatePersistentDiskProps {
  handle: string;
  size: number;
  diskType?: string;
  envId: string;
}

// v1 supports only gp3; deploy-api requires disk_type (validates inclusion in
// SUPPORTED_DISK_TYPES with no allow_nil), so an omitted type 422s on create.
export const DEFAULT_PERSISTENT_DISK_TYPE = "gp3";

/**
 * Builds the snake_case create request body. Callers may override disk_type;
 * otherwise it defaults to gp3.
 */
export const createPersistentDiskBody = ({
  handle,
  size,
  diskType,
}: Pick<CreatePersistentDiskProps, "handle" | "size" | "diskType">): Record<
  string,
  string | number
> => ({
  handle,
  size,
  disk_type: diskType || DEFAULT_PERSISTENT_DISK_TYPE,
});

/**
 * Creates a persistent_disk record. Use `provisionPersistentDisk` to
 * create-and-provision in a single saga.
 */
export const createPersistentDisk = api.post<
  CreatePersistentDiskProps,
  DeployPersistentDiskResponse
>("/accounts/:envId/persistent_disks", function* (ctx, next) {
  const body = createPersistentDiskBody(ctx.payload);
  ctx.request = ctx.req({ body: JSON.stringify(body) });
  yield* next();
});

interface ProvisionPersistentDiskOpProps {
  id: string;
  type: "provision";
}

interface DeprovisionPersistentDiskOpProps {
  id: string;
  type: "deprovision";
}

interface AttachPersistentDiskOpProps {
  id: string;
  type: "attach";
  serviceId: string;
  mountPoint: string;
}

interface ModifyPersistentDiskOpProps {
  id: string;
  type: "modify";
  // New size in GB. Maps to the operation's `disk_size` field, which the
  // PersistentDisk::Modify play reads as the target size.
  size: number;
  // Optional new provisioned IOPS; deploy-api defaults this from the size
  // when omitted, so only send it when the caller explicitly sets it.
  provisionedIops?: number;
}

export type PersistentDiskOpProps =
  | ProvisionPersistentDiskOpProps
  | DeprovisionPersistentDiskOpProps
  | AttachPersistentDiskOpProps
  | ModifyPersistentDiskOpProps;

/**
 * Builds the snake_case operation request body for a persistent_disk operation,
 * mapping each op type's fields to the names deploy-api expects.
 */
export const persistentDiskOperationBody = (
  payload: PersistentDiskOpProps,
): Record<string, string | number> => {
  switch (payload.type) {
    case "provision":
    case "deprovision":
      return { type: payload.type };
    case "attach":
      return {
        type: payload.type,
        service_id: payload.serviceId,
        mount_point: payload.mountPoint,
      };
    case "modify": {
      // deploy-api reads the target size and IOPS off the operation's
      // disk_size / provisioned_iops fields.
      const body: Record<string, string | number> = {
        type: payload.type,
        disk_size: payload.size,
      };
      // deploy-api defaults provisioned_iops from the size when omitted, so only
      // send it when the caller explicitly set it.
      if (payload.provisionedIops != null) {
        body.provisioned_iops = payload.provisionedIops;
      }
      return body;
    }
    default:
      return {};
  }
};

export const createPersistentDiskOperation = api.post<
  PersistentDiskOpProps,
  DeployOperationResponse
>("/persistent_disks/:id/operations", function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(persistentDiskOperationBody(ctx.payload)),
  });
  yield* next();
});

// `thunkLoader` publishes the name-keyed loader state that `useLoader(creator)`
// watches; failures are thrown so the loader records the error message.
export const provisionPersistentDisk = thunks.create<CreatePersistentDiskProps>(
  "persistent-disk-provision",
  [
    thunkLoader,
    function* (ctx, next) {
      const createCtx = yield* call(() =>
        createPersistentDisk.run(createPersistentDisk(ctx.payload)),
      );
      if (!createCtx.json.ok) {
        const message = createCtx.json.error.message;
        ctx.json = { error: message, diskId: "", opId: "" };
        throw new Error(message);
      }
      const diskId = `${createCtx.json.value.id}`;

      const opCtx = yield* call(() =>
        createPersistentDiskOperation.run(
          createPersistentDiskOperation({ id: diskId, type: "provision" }),
        ),
      );

      if (!opCtx.json.ok) {
        const message = opCtx.json.error.message;
        ctx.json = { error: message, diskId, opId: "" };
        throw new Error(message);
      }

      const opId = `${opCtx.json.value.id}`;
      ctx.json = { error: "", diskId, opId };
      ctx.loader = { meta: { diskId, opId } };
      yield* next();
    },
  ],
);

export interface ModifyPersistentDiskProps {
  id: string;
  size: number;
  provisionedIops?: number;
}

/**
 * Resizes (and optionally re-IOPS) a persistent_disk by creating a `modify`
 * operation. The UI polls the returned operation to track progress.
 */
export const modifyPersistentDisk = thunks.create<ModifyPersistentDiskProps>(
  "persistent-disk-modify",
  [
    thunkLoader,
    function* (ctx, next) {
      const { id, size, provisionedIops } = ctx.payload;
      const opCtx = yield* call(() =>
        createPersistentDiskOperation.run(
          createPersistentDiskOperation({
            id,
            type: "modify",
            size,
            provisionedIops,
          }),
        ),
      );

      if (!opCtx.json.ok) {
        const message = opCtx.json.error.message;
        ctx.json = { error: message, opId: "" };
        throw new Error(message);
      }

      const opId = `${opCtx.json.value.id}`;
      ctx.json = { error: "", opId };
      ctx.loader = { meta: { opId } };
      yield* next();
    },
  ],
);

export interface AttachPersistentDiskProps {
  id: string;
  serviceId: string;
  mountPoint: string;
}

/**
 * Attaches a persistent_disk to a service by creating an `attach` operation.
 * The DiskAttachment record is created backend-side when the operation
 * completes; the UI polls the returned operation to track progress.
 */
export const attachPersistentDisk = thunks.create<AttachPersistentDiskProps>(
  "persistent-disk-attach",
  [
    thunkLoader,
    function* (ctx, next) {
      const { id, serviceId, mountPoint } = ctx.payload;
      const opCtx = yield* call(() =>
        createPersistentDiskOperation.run(
          createPersistentDiskOperation({
            id,
            type: "attach",
            serviceId,
            mountPoint,
          }),
        ),
      );

      if (!opCtx.json.ok) {
        const message = opCtx.json.error.message;
        ctx.json = { error: message, opId: "" };
        throw new Error(message);
      }

      const opId = `${opCtx.json.value.id}`;
      ctx.json = { error: "", opId };
      ctx.loader = { meta: { opId } };
      yield* next();
    },
  ],
);

export interface CreateAndAttachPersistentDiskProps {
  envId: string;
  handle: string;
  size: number;
  serviceId: string;
  mountPoint: string;
}

/**
 * Service-originated "create new disk and attach" flow. Composes existing
 * endpoints only (no new deploy-api surface): creates the persistent_disk
 * record, fires a `provision` operation, then fires an `attach` operation
 * referencing the target service + mount point. sweetness sequences the
 * operations on the disk; the DiskAttachment record is created as a side
 * effect when the attach op completes.
 */
export const createAndAttachPersistentDisk =
  thunks.create<CreateAndAttachPersistentDiskProps>(
    "persistent-disk-create-and-attach",
    [
      thunkLoader,
      function* (ctx, next) {
        const { envId, handle, size, serviceId, mountPoint } = ctx.payload;

        const createCtx = yield* call(() =>
          createPersistentDisk.run(
            createPersistentDisk({ envId, handle, size }),
          ),
        );
        if (!createCtx.json.ok) {
          const message = createCtx.json.error.message;
          ctx.json = { error: message, diskId: "", opId: "" };
          throw new Error(message);
        }
        const diskId = `${createCtx.json.value.id}`;

        const provisionCtx = yield* call(() =>
          createPersistentDiskOperation.run(
            createPersistentDiskOperation({ id: diskId, type: "provision" }),
          ),
        );
        if (!provisionCtx.json.ok) {
          const message = provisionCtx.json.error.message;
          ctx.json = { error: message, diskId, opId: "" };
          throw new Error(message);
        }

        const attachCtx = yield* call(() =>
          createPersistentDiskOperation.run(
            createPersistentDiskOperation({
              id: diskId,
              type: "attach",
              serviceId,
              mountPoint,
            }),
          ),
        );

        if (!attachCtx.json.ok) {
          const message = attachCtx.json.error.message;
          ctx.json = { error: message, diskId, opId: "" };
          throw new Error(message);
        }

        const opId = `${attachCtx.json.value.id}`;
        ctx.json = { error: "", diskId, opId };
        ctx.loader = { meta: { diskId, opId } };
        yield* next();
      },
    ],
  );

export const persistentDiskEntities = {
  persistent_disk: defaultEntity({
    id: "persistent_disk",
    deserialize: deserializePersistentDisk,
    save: schema.persistentDisks.add,
  }),
};
